import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { LanguageField } from '@app/core/components/LanguageField';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FormProvider, TextFieldElement, useForm } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { UserSettingsValues, getUserSettingsSchema } from './user-settings.schema';

interface Props {
  values: UserSettingsValues;
  onSubmit: (data: UserSettingsValues) => Promise<any>;
}

export function UserSettingsForm({ values, onSubmit }: Props) {
  const { $t } = useIntl();
  const schema = useMemo(() => getUserSettingsSchema((msg) => $t(msg)), [$t]);
  const methods = useForm<UserSettingsValues>({
    defaultValues: values,
    resolver: zodResolver(schema),
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(values);
  }, [values, reset]);

  /** Submit handler: calls parent onSubmit then resets form with submitted data so isDirty becomes false and save button can show tick. */
  const handleSuccess = async (data: UserSettingsValues) => {
    await onSubmit(data);
    reset(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSuccess)} noValidate>
        <Stack spacing={4} useFlexGap mt={2}>
          <Grid container spacing={2}>
            <Grid item md={3}>
              <TextFieldElement
                name="name"
                label={$t({
                  id: 'settings.userSettings.form.name.label',
                  defaultMessage: 'Nombre',
                })}
                fullWidth
                required
              />
            </Grid>
            <Grid item md={3}>
              <TextFieldElement
                name="surname"
                label={$t({
                  id: 'settings.userSettings.form.surname.label',
                  defaultMessage: 'Apellido',
                })}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item md={4}>
              <LanguageField name="language" required />
            </Grid>
          </Grid>

          <FormActionsMui successState />
        </Stack>
      </form>
    </FormProvider>
  );
}
